import {
  AppBar,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef, GridRowsProp } from '@mui/x-data-grid'
import { SIDEBAR_WIDTH } from 'components/CommonSidebar'
import { NAVBAR_HEIGHT } from 'components/Navbar'
import {
  filterReducer,
  filterReports,
  initFilters,
  ReportFilters,
} from 'components/ReportFilters'
import ShareDialog from 'components/ShareDialog'
import {
  useGetManagedReportsByYearQuery,
  useGetReportsByYearQuery,
  useGetReportTaggingsByYearQuery,
} from 'gqlTypes'
import React, { useMemo, useReducer, useState } from 'react'
import { Link } from 'react-router-dom'
import { getReportTypeTag, getYearTag } from 'util/getReportTags'

type Props = {
  years: number[]
  selectedYear: number
  onUpdateYear: (event: SelectChangeEvent) => void
}

const ReportsYearContent: React.FC<Props> = ({
  years,
  selectedYear,
  onUpdateYear,
}) => {
  const [shareOpen, setShareOpen] = useState(false)
  const [shareReports, setShareReports] = useState<GridRowsProp>([])
  const [pageSize, setPageSize] = useState(10)

  const { data, loading, error } = useGetReportsByYearQuery({
    variables: { year: selectedYear },
  })

  const {
    data: filterData,
    loading: filterLoading,
    error: filterError,
  } = useGetReportTaggingsByYearQuery({
    variables: { year: selectedYear },
  })

  const {
    data: managementData,
    loading: managementLoading,
    error: managementError,
  } = useGetManagedReportsByYearQuery({
    variables: { year: selectedYear },
  })

  const [filters, filterDispatch] = useReducer(
    filterReducer,
    undefined,
    initFilters
  )

  const handleChangeYear = (event: SelectChangeEvent) => {
    filterDispatch({ type: 'clearFilters' })
    onUpdateYear(event)
  }

  const reports = useMemo(
    () =>
      (data &&
        filterReports(
          data.reportsByYear,
          filters,
          filterData?.reportTaggingsByYear || []
        )) ||
      [],
    [data, filters, filterData]
  )

  const handleCloseShare = () => {
    setShareOpen(false)
    setShareReports([])
  }

  const rows = useMemo(() => {
    return (
      reports.map((r) => ({
        id: r.id,
        title: r.title,
        reportType: getReportTypeTag(
          r.id,
          filterData?.reportTaggingsByYear || []
        ),
        year: getYearTag(r.id, filterData?.reportTaggingsByYear || []),
        share: (managementData?.managedReportsByYear || []).some(
          (d) => d.reportId === r.id
        ),
      })) ?? []
    )
  }, [reports, filterData, managementData])
  const shareCount = rows.filter((r) => r.share).length

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Report Name',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        return (
          <Link
            style={{
              textDecoration: 'none',
              color: 'inherit',
            }}
            to={`/reports/${params.row.id}/`}
            title={params.row.title}
          >
            {params.row.title}
          </Link>
        )
      },
    },
    { field: 'reportType', headerName: 'Report Type', width: 200 },
    { field: 'year', headerName: 'Year', width: 200 },
    {
      field: 'share',
      headerName: '',
      width: 100,
      renderCell: (params) => {
        if (params.row.share) {
          return (
            <Button
              onClick={() => {
                setShareReports([params.row])
                setShareOpen(true)
              }}
            >
              Share
            </Button>
          )
        }
        return ''
      },
      sortingOrder: ['desc', null],
    },
  ]

  return (
    <Box display="flex">
      {!filterError && (
        <ReportFilters
          reports={data?.reportsByYear || []}
          filters={filters}
          dispatch={filterDispatch}
          reportTaggings={filterData?.reportTaggingsByYear || []}
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          background: '#f6f6f6',
          width: 15,
          top: 0,
          bottom: 0,
          left: SIDEBAR_WIDTH,
        }}
      />
      <Box sx={{ minWidth: 15 }} />
      <Box sx={{ width: '100%', position: 'relative' }}>
        <AppBar
          position="sticky"
          elevation={1}
          sx={{
            backgroundColor: 'white',
            top: NAVBAR_HEIGHT,
            py: 3,
            px: 6,
            zIndex: 1,
          }}
        >
          <FormControl size="small" sx={{ width: '160px' }}>
            <InputLabel id="display-year-label">Year</InputLabel>
            <Select
              labelId="display-year-label"
              id="display-year-input"
              value={`${selectedYear}`}
              label="Year"
              onChange={handleChangeYear}
            >
              {years.map((year) => {
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="h2" component="h1">
              My Reports
            </Typography>

            {data && (
              <Typography
                component="h1"
                variant="h4"
                sx={{ color: 'primary.light' }}
              >
                ({data.reportsByYear.length} total)
              </Typography>
            )}
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="end">
            {!error && (
              <Typography mt={1} color="primary">
                {selectedYear === years[0]
                  ? 'These are your most recent reports. To explore older reports, choose another year from the selector above.'
                  : 'You’ve navigated to older reports. Switch years anytime using the dropdown above.'}
              </Typography>
            )}
            <Box>
              {shareCount > 0 && (
                <Tooltip title="Refine reports to share by filtering.">
                  <Button
                    onClick={() => {
                      setShareReports(rows.filter((r) => r.share))
                      setShareOpen(true)
                    }}
                  >
                    {`Share All Reports (${shareCount})`}
                  </Button>
                </Tooltip>
              )}
            </Box>
          </Box>
        </AppBar>
        <Box sx={{ p: 6, pt: 3, mx: 2 }}>
          {error && (
            <Box>
              There was an error loading your reports. Please reload the page
              and try again.
            </Box>
          )}
          {loading && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}
          {rows.length > 0 && (
            <Stack spacing={2}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignContent="center"
              >
                <Typography>
                  Showing{' '}
                  <b>
                    {rows.length > pageSize ? pageSize : rows.length} of{' '}
                    {rows.length}
                  </b>{' '}
                  reports
                </Typography>
              </Box>
              <DataGrid
                disableSelectionOnClick
                disableColumnMenu
                autoHeight
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={[10, 25, 50]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                initialState={{}}
                sx={{
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
              />
            </Stack>
          )}
          <ShareDialog
            open={shareOpen}
            reports={shareReports}
            onClose={handleCloseShare}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default ReportsYearContent
